import React from 'react';
import { Shield, FileText, ExternalLink } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Omnicraft Services</title>
        <meta name="description" content="Read Omnicraft Services' privacy policy. Learn how we collect, use, and protect your personal information." />
      </Helmet>
      <div className="min-h-screen pt-16">
        {/* Hero Section */}
        <section className="py-12 bg-gradient-to-b from-primary/10 to-transparent dark:from-primary/5">
          <div className="container-custom">
            <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
              <div className="p-3 rounded-full bg-primary/10 mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h1 className="mb-4">Privacy Policy</h1>
              <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 mb-6">
                We take your privacy seriously. This policy outlines how we collect, use, and protect your personal information.
              </p>
              <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <FileText size={16} />
                <span>Last updated: April 18, 2025</span>
              </div>
            </div>
          </div>
        </section>

        {/* Policy Content */}
        <section className="py-16">
          <div className="container-custom max-w-4xl">
            <div className="bg-white dark:bg-slate-900 rounded-xl p-6 md:p-10 shadow-md dark:shadow-slate-800/40">
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
                  <p className="text-slate-600 dark:text-slate-400">
                    OmniCraftServices Services ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                  </p>
                </div>
                
                <Separator />
                
                <div>
                  <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
                  <h3 className="text-xl font-medium mb-2">2.1. Personal Information</h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    We may collect personal information that you voluntarily provide to us when you:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-400">
                    <li>Register on our website</li>
                    <li>Subscribe to our newsletters</li>
                    <li>Fill out a contact form</li>
                    <li>Request a quote or services</li>
                    <li>Participate in surveys or promotions</li>
                    <li>Submit payment information</li>
                  </ul>
                  
                  <h3 className="text-xl font-medium mt-6 mb-2">2.2. Non-Personal Information</h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    When you visit our website, we may automatically collect certain information about your device, including:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-400">
                    <li>IP address</li>
                    <li>Browser type and version</li>
                    <li>Operating system</li>
                    <li>Time spent on pages</li>
                    <li>Pages visited</li>
                    <li>Referral sources</li>
                    <li>Interaction data and click patterns</li>
                  </ul>
                </div>
                
                <Separator />
                
                <div>
                  <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    We may use the information we collect for various purposes, including:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-400">
                    <li>Providing, maintaining, and improving our services</li>
                    <li>Processing transactions and sending related information</li>
                    <li>Sending administrative messages and updates</li>
                    <li>Responding to inquiries and offering support</li>
                    <li>Marketing and promotional purposes</li>
                    <li>Analyzing usage patterns and trends</li>
                    <li>Protecting against fraud and unauthorized access</li>
                  </ul>
                </div>
                
                <Separator />
                
                <div>
                  <h2 className="text-2xl font-semibold mb-4">4. Cookies and Tracking Technologies</h2>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    We use cookies and similar tracking technologies to collect information about your browsing activities and to improve your experience on our website. You can manage your cookie preferences through your browser settings.
                  </p>
                </div>
                
                <Separator />
                
                <div>
                  <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </div>
                
                <Separator />
                
                <div>
                  <h2 className="text-2xl font-semibold mb-4">6. Third-Party Services</h2>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    We may use third-party service providers to help us operate our business and administer activities on our behalf. These third parties may have access to your personal information only to perform specific tasks on our behalf and are obligated not to disclose or use it for any other purpose.
                  </p>
                </div>
                
                <Separator />
                
                <div>
                  <h2 className="text-2xl font-semibold mb-4">7. Your Rights</h2>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    Depending on your location, you may have the following rights regarding your personal information:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-400">
                    <li>Access to your personal information</li>
                    <li>Correction of inaccurate or incomplete information</li>
                    <li>Deletion of your personal information</li>
                    <li>Restriction of processing</li>
                    <li>Data portability</li>
                    <li>Objection to processing</li>
                    <li>Withdrawal of consent</li>
                  </ul>
                </div>
                
                <Separator />
                
                <div>
                  <h2 className="text-2xl font-semibold mb-4">8. Changes to This Privacy Policy</h2>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                  </p>
                </div>
                
                <Separator />
                
                <div>
                  <h2 className="text-2xl font-semibold mb-4">9. Contact Us</h2>
                  <p className="text-slate-600 dark:text-slate-400 mb-6">
                    If you have any questions or concerns about this Privacy Policy, please contact us at:
                  </p>
                  <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                    <p className="font-medium mb-1">OmniCraftServices Services</p>
                    <p className="text-slate-600 dark:text-slate-400">Email: contact@omnicraftservices.com</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-800">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <Button variant="outline" asChild>
                    <Link to="/" className="flex items-center gap-2">
                      <span>Return to Home</span>
                    </Link>
                  </Button>
                  <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <FileText size={14} />
                    <span>PDF version available</span>
                    <ExternalLink size={14} className="ml-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;
